import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { BodySmall } from '../Texts';
import Dropdown from '../Dropdown';
import Button from '../Button';
import LanguageSelector from '../LanguageSelector';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';
import { selectTotalWalletBalanceUSD } from '../../redux/slices/tokenSlice';
import { IonButtons, IonMenuButton } from '@ionic/react';
import WalletConnectButton from '../WalletConnect';
import { useFlag } from '@unleash/proxy-client-react';
import { hapticsImpactLight } from '../../common/haptic';
import useCustomLogin from '../../hooks/useCustomLogin';
import DropdownContent from './DropdownContent';
import { useDispatch, useSelector } from 'react-redux';
import { Capacitor } from '@capacitor/core';
import { selectThemeMode } from '../../redux/slices/themeSlice';
import useOpenApp from '../../hooks/useOpenApp';
import SpendCountrySelector from '../SpendCountrySelector';
import { getCountryFlag, getCountryName } from '../../common/countries';
import {
  selectSelectedCountryCode,
  setSelectedCountryCode,
} from '../../redux/slices/giftCardCatalogue';
import {
  EVENT_CHECKOUT_LANDING_PAGE_VISITED,
  sendEvent,
} from '../../common/analytics';

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-left: auto;
  margin-right: 24px;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 12px;
    margin-right: 12px;
  }
`;

const SpendNavContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SpendSelectorWrapper = styled.div`
  min-width: 220px;
`;

const MobileSpendSelectorWrapper = styled.div`
  min-width: 64px;
  @media ${PHONE_MEDIA_QUERY} {
    min-width: 36px;
  }
`;

const NavLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const NavUserProfileContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  @media ${PHONE_MEDIA_QUERY} {
    margin-left: auto;
    justify-content: flex-end;
    width: 100%;
  }
`;

const ProfileTrigger = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const BalanceContainer = styled.div`
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  padding: 10px;
  background: ${({ theme }) => theme.background.secondary};
  min-height: 46px;
  border-radius: 8px;
`;

const BlackLayer = styled.div`
  width: 101vw;
  height: 100vh;
  background-color: ${({ theme }) => theme.background.overlay};
  top: 100%;
  left: -16px;
  right: 0px;
  bottom: 0px;
  z-index: 999;
  position: absolute;
  backdrop-filter: blur(2px);
`;

export const VerticalLine = styled.div`
  height: 20px;
  width: 1px;
  background-color: ${({ theme }) => theme.border.dashed};
`;

const BurgerMenuContainer = styled.div`
  margin-right: auto;
`;

const NavUserProfile = ({ user }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const { tokenName } = useParams();
  const [searchParams] = useSearchParams();
  const lpd = searchParams.get('lpd');
  const [isOpen, setIsOpen] = useState(false);

  const totalBalance = useSelector(selectTotalWalletBalanceUSD);
  const dispatch = useDispatch();
  const selectedCountryCode = useSelector(selectSelectedCountryCode);
  const selectedFlag = selectedCountryCode
    ? getCountryFlag(selectedCountryCode)
    : '';
  const selectedCountryName = selectedCountryCode
    ? getCountryName(selectedCountryCode)
    : '';
  const { login } = useCustomLogin();
  const navigate = useNavigate();

  const location = useLocation();

  const isClaimPage = location.pathname.includes('claim'); // promo-claim or claim

  const enableWalletConnect =
    useFlag('enableWalletConnect') &&
    !Capacitor.isNativePlatform() &&
    !isClaimPage;
  const enableSpend = useFlag('enableSpend');

  const themeMode = useSelector(selectThemeMode);
  const theme = useTheme();
  const { openApp } = useOpenApp();

  const showOpenAppButton =
    !Capacitor.isNativePlatform() &&
    (location.pathname.startsWith('/app/home') ||
      location.pathname.startsWith('/app/portfolio'));
  const isSpendActive = location.pathname.startsWith('/app/spend');
  const isCheckout = location.pathname.startsWith('/checkout');
  const isSpendLanding = location.pathname === '/' && lpd === 'spend';
  const isYieldFlow = lpd === 'yield';
  const useFullLogo = true;
  const showLogo =
    !isMobile || !user || isSpendLanding || (isYieldFlow && !!user);

  return (
    <>
      <NavContainer>
        {showLogo && (
          <>
            <img
              style={{
                width: useFullLogo ? 120 : 40,
                cursor: isYieldFlow ? 'default' : 'pointer',
              }}
              src={
                useFullLogo
                  ? themeMode === 'light'
                    ? '/logo.webp'
                    : '/logo-dark.webp'
                  : '/mobile-logo.webp'
              }
              onClick={
                isYieldFlow
                  ? undefined
                  : () => {
                      hapticsImpactLight();
                      if (isCheckout) {
                        sendEvent(EVENT_CHECKOUT_LANDING_PAGE_VISITED);
                        navigate(`/?lpd=checkout-${tokenName}`);
                      } else {
                        if (user) {
                          navigate('/app/home');
                        } else {
                          if (lpd === 'spend') {
                            navigate('/?lpd=spend');
                          } else {
                            navigate('/');
                          }
                        }
                      }
                    }
              }
              role={isYieldFlow ? undefined : 'button'}
            />
            {enableSpend && !!user && !isYieldFlow && (
              <NavLinks>
                <SpendNavContainer>
                  {isSpendActive && (
                    <SpendSelectorWrapper>
                      <SpendCountrySelector
                        value={selectedCountryCode}
                        onChange={(countryCode) =>
                          dispatch(setSelectedCountryCode(countryCode))
                        }
                        label=''
                        minWidth='64px'
                        selectedLabelOverride={isMobile ? selectedFlag : null}
                      />
                    </SpendSelectorWrapper>
                  )}
                  {/* <NavLink
                    type="button"
                    $active={isSpendActive}
                    onClick={() => navigate('/app/spend')}
                  >
                    <img
                      src="/cart.svg"
                      alt=""
                      style={{ width: 16, height: 16 }}
                    />
                    <BodySmall
                      $variant="text-title"
                      style={{ color: 'inherit' }}
                    >
                      Spend
                    </BodySmall>
                  </NavLink> */}
                </SpendNavContainer>
              </NavLinks>
            )}
          </>
        )}
        {!!user && !isYieldFlow ? (
          <NavUserProfileContainer>
            {isMobile && (
              <BurgerMenuContainer
                onClick={() => {
                  hapticsImpactLight();
                }}
              >
                <IonButtons slot='start'>
                  <IonMenuButton
                    style={{ color: theme.text.title }}
                  ></IonMenuButton>
                </IonButtons>
              </BurgerMenuContainer>
            )}
            {isMobile && enableSpend && isSpendActive && (
              <>
                <MobileSpendSelectorWrapper>
                  <SpendCountrySelector
                    value={selectedCountryCode}
                    onChange={(countryCode) =>
                      dispatch(setSelectedCountryCode(countryCode))
                    }
                    label=''
                    minWidth='56px'
                    selectedLabelOverride={
                      selectedFlag && selectedCountryName
                        ? `${selectedFlag} ${selectedCountryName}`
                        : selectedFlag
                    }
                    selectedValueStyle={{
                      fontSize: '16px',
                      lineHeight: '20px',
                      color: theme.text.title,
                    }}
                    hideChevron={true}
                  />
                </MobileSpendSelectorWrapper>
              </>
            )}
            {showOpenAppButton && isMobile && (
              <Button
                style={{ height: '36px' }}
                onClick={() => {
                  hapticsImpactLight();
                  if (isMobile) {
                    openApp();
                  } else {
                    window.open(
                      'https://deks.xyz/download',
                      '_blank',
                      'noopener,noreferrer',
                    );
                  }
                }}
              >
                {t('profile.downloadApp')}
              </Button>
            )}
            {/* {!isMobile && (
              <>
                <BodyMedium
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    hapticsImpactLight();
                    navigate('/rewards');
                  }}
                >
                  Rewards
                </BodyMedium>
                <VerticalLine />
              </>
            )} */}
            {enableWalletConnect && (
              <>
                <WalletConnectButton
                  onConnectedClick={() => {
                    setIsOpen(true);
                  }}
                />
              </>
            )}
            {!isMobile && <VerticalLine />}
            {!isMobile && (
              <>
                <LanguageSelector />
                <VerticalLine />
                <Dropdown
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                  trigger={
                    <ProfileTrigger>
                      <BodySmall>
                        {!isMobile ? (
                          <>
                            {(() => {
                              const email =
                                user?.email?.address ||
                                user?.google?.email ||
                                user?.email;

                              if (!email) return user?.phone?.number || '';

                              const [name, domain] = email.split('@');
                              return (
                                <>
                                  {name}
                                  <span style={{ userSelect: 'none' }}>
                                    &#8203;
                                  </span>
                                  <span>@</span>
                                  {domain}
                                </>
                              );
                            })()}
                          </>
                        ) : (
                          <>
                            <img
                              src='/navbar-user.webp'
                              width='24'
                              height='24'
                            />
                          </>
                        )}
                      </BodySmall>
                    </ProfileTrigger>
                  }
                >
                  <DropdownContent setIsOpen={setIsOpen} />
                </Dropdown>
              </>
            )}
            {isMobile && <LanguageSelector />}
          </NavUserProfileContainer>
        ) : (
          <NavUserProfileContainer style={{ gap: isMobile ? 10 : 30 }}>
            <LanguageSelector />
            {/* <a
              style={{ textDecoration: 'none', color: 'inherit' }}
              onClick={(e) => {
                navigate('/gift-cards');
              }}
            >
              <BodyMedium
                style={{
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: 'clamp(12px, .5vw, 13px)',
                }}
              >
                Partners
              </BodyMedium>
            </a>
            <VerticalLine /> */}
            {enableWalletConnect && <WalletConnectButton />}
            {!isYieldFlow && !isCheckout && (
              <Button
                style={{ border: 'none', color: theme.text.light }}
                variant='secondary'
                onClick={() => {
                  login();
                }}
              >
                {t('nav.logIn')}
              </Button>
            )}
          </NavUserProfileContainer>
        )}
      </NavContainer>
      {isMobile && isOpen && <BlackLayer />}
    </>
  );
};

export default NavUserProfile;
